import type React from 'react';
import { createRef, useCallback, useEffect } from 'react';
import ReactTextMaskComponent from 'react-text-mask';
import { createNumberMask } from 'text-mask-addons';

const MaskedInput =
  typeof ReactTextMaskComponent === 'function'
    ? ReactTextMaskComponent
    : (
        ReactTextMaskComponent as unknown as {
          default: typeof ReactTextMaskComponent;
        }
      ).default;

type MaskedInputRef = InstanceType<typeof MaskedInput> & {
  initTextMask?: () => void;
};

interface IMaskOptions {
  prefix?: string;
  allowDecimal?: boolean;
  allowNegative?: boolean;
  allowLeadingZeroes?: boolean;
  includeThousandsSeparator?: boolean;
  thousandsSeparatorSymbol?: string;
  decimalSymbol?: string;
  integerLimit?: number;
  decimalLimit?: number;
  requireDecimal?: boolean;
}

interface INumberMaskInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  maskOptions?: IMaskOptions;
  minValue?: number;
  maxValue?: number;
  value?: string | number;
  onChange: (value: string) => void;
}

const defaultMaskOptions: IMaskOptions = {
  prefix: '',
  allowDecimal: false,
  allowNegative: false,
  allowLeadingZeroes: true,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
};

export function NumberMaskInput({
  maskOptions,
  minValue,
  maxValue,
  value: passedValue,
  onChange,
  ...inputProps
}: INumberMaskInputProps) {
  const value =
    typeof passedValue === 'number' || typeof passedValue === 'string' ? passedValue : 0;

  const ref = createRef<MaskedInputRef>();

  const numberMask = createNumberMask({
    ...defaultMaskOptions,
    ...(maskOptions || {}),
  });

  useEffect(() => {
    ref.current?.initTextMask?.();
  }, [maxValue, minValue]);

  const onPipe = useCallback(
    (confirmedValue: string): string | false => {
      const numberToTest = confirmedValue.replace(/,/g, '');
      const numericValue = Number(numberToTest);

      if (minValue === undefined && maxValue === undefined) return confirmedValue;

      if (
        minValue !== undefined &&
        maxValue !== undefined &&
        !isNaN(numericValue) &&
        numericValue >= minValue &&
        numericValue <= maxValue
      )
        return confirmedValue;

      if (
        minValue !== undefined &&
        maxValue === undefined &&
        !isNaN(numericValue) &&
        numericValue >= minValue
      )
        return confirmedValue;

      if (
        maxValue !== undefined &&
        minValue === undefined &&
        !isNaN(numericValue) &&
        numericValue <= maxValue
      )
        return confirmedValue;

      if ([' ', '-', '_'].includes(confirmedValue)) return confirmedValue;

      return false;
    },
    [maxValue, minValue],
  );

  return (
    <MaskedInput
      ref={ref}
      mask={numberMask}
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.value.replace(/[^0-9.]+/g, ''))
      }
      pipe={onPipe}
      {...(inputProps as any)}
    />
  );
}
